import React from 'react';
import { Icon } from '../../../pages/DesignSystem';
import type { EventMetrics, SeriesMetrics } from '../../../types/organizer-events.types';

interface EventCardKPIsProps {
  metrics: EventMetrics | SeriesMetrics | null;
  isLoading?: boolean;
  variant?: 'compact' | 'expanded';
  isSeries?: boolean;
}

/**
 * Get impressions value from metrics (handles both EventMetrics and SeriesMetrics)
 */
const getImpressions = (metrics: EventMetrics | SeriesMetrics | null, isSeries: boolean): number => {
  if (!metrics) return 0;
  if (isSeries && 'totalImpressions' in metrics) {
    return (metrics as SeriesMetrics).totalImpressions;
  }
  return (metrics as EventMetrics).impressions;
};

/**
 * Get the storytelling label for views
 */
const getViewsLabel = (impressions: number): string => {
  if (impressions === 0) return 'No views yet';
  return `Seen by ${impressions} ${impressions === 1 ? 'tourist' : 'tourists'}`;
};

/**
 * Custom comparison function for React.memo
 * Only re-render if the actual impressions VALUE changes
 */
const arePropsEqual = (
  prevProps: EventCardKPIsProps,
  nextProps: EventCardKPIsProps
): boolean => {
  if (prevProps.variant !== nextProps.variant || prevProps.isSeries !== nextProps.isSeries) {
    return false;
  }
  const prevImpressions = getImpressions(prevProps.metrics, prevProps.isSeries ?? false);
  const nextImpressions = getImpressions(nextProps.metrics, nextProps.isSeries ?? false);
  return prevImpressions === nextImpressions;
};

/**
 * EventCardKPIs - Displays "Seen by X tourists" for an event or series
 * Identical display on mobile (compact) and desktop (expanded)
 */
const EventCardKPIs: React.FC<EventCardKPIsProps> = ({
  metrics,
  isLoading = false,
  variant = 'compact',
  isSeries = false
}) => {
  const impressions = getImpressions(metrics, isSeries);

  // Show skeleton only on initial load (no data yet)
  const showSkeleton = isLoading && !metrics;

  if (showSkeleton) {
    return (
      <div className={`event-card-kpis event-card-kpis--${variant} event-card-kpis--loading`}>
        <div className="event-card-kpis__skeleton" />
      </div>
    );
  }

  const isRefreshing = isLoading && metrics;

  return (
    <div className={`event-card-kpis event-card-kpis--${variant} ${isRefreshing ? 'event-card-kpis--refreshing' : ''}`}>
      <div className="event-card-kpis__item" title="Views">
        <Icon name="Eye" size={14} />
        <span className="event-card-kpis__text">
          {getViewsLabel(impressions)}
        </span>
      </div>
    </div>
  );
};

export default React.memo(EventCardKPIs, arePropsEqual);
